import type { ReactElement } from "react";
import { lvClass } from "../lib/fit";

// Answer pictograms from the demo: a ring of fabric around the body (tight -> roomy),
// a pant leg over the floor, a hem against a reference line, a neckline and a shoulder seam.
// `f` is the step (-2..2).

function pgRing(f: number): ReactElement[] {
  const r = Math.max(8, 16 + f * 3.4);
  const out: ReactElement[] = [<circle key="b" className="pg-body" cx="32" cy="23" r="13.5" />];
  if (f > 0) out.push(<circle key="f" className="pg-fill" cx="32" cy="23" r={r} />);
  out.push(<circle key="r" className={"pg-fab" + (f > 0.5 ? " dash" : "")} cx="32" cy="23" r={r} />);
  if (f < 0)
    [45, 135, 225, 315].forEach((a) => {
      const rad = (a * Math.PI) / 180;
      const c = Math.cos(rad);
      const s = Math.sin(rad);
      const len = f <= -1.5 ? 7 : 4.5;
      out.push(
        <path
          key={"t" + a}
          className="pg-fab"
          d={`M${(32 + c * (r + 2)).toFixed(1)} ${(23 + s * (r + 2)).toFixed(1)} L${(32 + c * (r + len)).toFixed(1)} ${(23 + s * (r + len)).toFixed(1)}`}
        />,
      );
    });
  if (f >= 1) out.push(<path key="e" className="pg-fab" d={`M${32 + 14.5} 23 H${32 + r - 1.5} M${32 - 14.5} 23 H${32 - r + 1.5}`} />);
  if (f === 0) out.push(<path key="c" className="pg-fab" d="M27 23.5 l3.5 3.5 l6.5 -7" />);
  return out;
}

function pgLeg(f: number): ReactElement[] {
  const hem = f >= 2 ? 42 : f >= 1 ? 39 : f >= 0 ? 35.5 : f > -1.5 ? 30 : 24;
  const leg = f <= -1.5 ? `M24 2 H38 L38 ${hem - 3} L24 ${hem} Z` : `M24 2 H38 V${hem} H24 Z`;
  const out = [
    <path key="fl" className="pg-floor" d="M6 43 H58" />,
    <path key="sh" className="pg" d="M33 43 V39 Q38 37 46 39.5 L47 43" style={{ stroke: "var(--muted)" }} />,
    <path key="rf" className="pg-ref" d="M14 35.5 H50" />,
    <path key="lf" className="pg-fill" d={leg} />,
    <path key="lg" className="pg-fab" d={leg} />,
  ];
  if (f >= 2) out.push(<path key="pool" className="pg-fab" d="M20 43 Q31 47 44 43" />);
  if (f >= 1 && f < 2) out.push(<path key="bunch" className="pg-fab" d="M24 34 l3 2 l3 -2 l3 2 l3 -2 l2 1.5" />);
  return out;
}

function pgLen(f: number): ReactElement[] {
  const hem = 30 + f * 5;
  const d = `M20 4 H44 L46 ${hem} H18 Z`;
  return [<path key="r" className="pg-ref" d="M10 30 H54" />, <path key="f" className="pg-fill" d={d} />, <path key="o" className="pg-fab" d={d} />];
}

function pgNeck(f: number): ReactElement[] {
  const depth = 20 + f * 4.5;
  const out = [
    <path key="b" className="pg-body" d="M8 16 Q32 8 56 16 V44 H8 Z" />,
    <path key="n" className="pg-fab" d={`M6 14 Q14 8 22 8 Q32 ${depth} 42 8 Q50 8 58 14`} />,
  ];
  if (f >= 1) out.push(<path key="g" className="pg-fab dash" d={`M24 ${depth - 4} Q32 ${depth + 6} 40 ${depth - 4}`} />);
  if (f <= -1) out.push(<path key="t" className="pg-fab" d="M26 4 l3 3 M38 4 l-3 3" />);
  return out;
}

function pgShoulder(f: number): ReactElement[] {
  const x = 40 + f * 6;
  return [
    <path key="b" className="pg-body" d="M4 44 V24 Q6 12 20 10 H40 Q46 11 46 18 V44 Z" />,
    <path key="s" className="pg-fab" d={`M2 16 Q10 8 22 8 H${x} L${x + 8} 30`} />,
    <path key="m" className="pg-fab" d={`M${x} 8 V24`} />,
    <circle key="c" cx="42" cy="12" r="2" style={{ fill: "var(--muted)" }} />,
  ];
}

export function Pictogram({ zone, step }: { zone: string; step: number }) {
  const inner =
    zone === "length" ? pgLeg(step) : /length$/.test(zone) ? pgLen(step) : zone === "neckline" ? pgNeck(step) : zone === "shoulder" ? pgShoulder(step) : pgRing(step);
  return (
    <span className={"pic " + lvClass(Math.round(Math.max(-2, Math.min(2, step))))}>
      <svg viewBox="0 0 64 46" aria-hidden="true">
        {inner}
      </svg>
    </span>
  );
}
